// src/services/approval.service.js
import { api } from './api';

const LOCAL_STORAGE_KEY = 'hrportal_employee_update_requests';

class ApprovalService {
    /**
     * Submit an employee update request for admin approval
     */
    async submitEmployeeUpdateRequest(payload) {
        try {
            const response = await api.post('/api/approvals/employee-update-requests', payload);
            return response.data;
        } catch (error) {
            console.error('Error submitting employee update approval request, falling back to local storage:', error);
            const request = this.saveRequestToLocal(payload);
            return {
                success: true,
                request,
                source: 'local'
            };
        }
    }

    /**
     * Get all employee update requests (admin view)
     */
    async getEmployeeUpdateRequests() {
        try {
            const response = await api.get('/api/approvals/employee-update-requests');
            return response.data;
        } catch (error) {
            console.error('Error fetching employee update approval requests, falling back to local storage:', error);
            const requests = this.getLocalRequests();
            return {
                success: true,
                requests,
                source: 'local'
            };
        }
    }

    /**
     * Get pending approvals count for sidebar badge
     * Returns: { leave, attendance, other, total }
     */
    async getPendingApprovalsCount() {
        try {
            const response = await api.get('/api/approvals/pending-count');
            return response.data;
        } catch (error) {
            console.error('Error fetching pending approvals count:', error);
            return {
                success: false,
                leave: 0,
                attendance: 0,
                other: 0,
                total: 0
            };
        }
    }

    /**
     * Approve a pending employee update request
     */
    async approveEmployeeUpdateRequest(requestId, { comments = '', reviewer = null } = {}) {
        return this.updateEmployeeUpdateRequestStatus(requestId, 'approved', comments, reviewer);
    }

    /**
     * Reject a pending employee update request
     */
    async rejectEmployeeUpdateRequest(requestId, { comments = '', reviewer = null } = {}) {
        return this.updateEmployeeUpdateRequestStatus(requestId, 'rejected', comments, reviewer);
    }

    /**
     * Update approval request status with optional reviewer metadata
     */
    async updateEmployeeUpdateRequestStatus(requestId, status, comments = '', reviewer = null) {
        try {
            const response = await api.put(`/api/approvals/employee-update-requests/${requestId}`, {
                status,
                comments
            });
            return response.data;
        } catch (error) {
            console.error('Error updating employee update approval status, falling back to local storage:', error);
            const updatedRequest = this.updateLocalRequestStatus(requestId, status, comments, reviewer);
            return {
                success: Boolean(updatedRequest),
                request: updatedRequest,
                source: 'local'
            };
        }
    }

    /**
     * Persist approval request locally when backend unavailable
     */
    saveRequestToLocal(payload) {
        const requests = this.getLocalRequests();
        const request = {
            id: payload.id || `local-${Date.now()}`,
            type: 'employee_update',
            status: 'pending',
            submitted_at: payload.submitted_at || new Date().toISOString(),
            submitted_by: payload.submitted_by,
            submitted_by_name: payload.submitted_by_name,
            submitted_by_role: payload.submitted_by_role,
            employee_id: payload.employee_id,
            employee_name: payload.employee_name,
            changes: payload.changes || [],
            updates: payload.updates || {},
            previous_values: payload.previous_values || {},
            metadata: payload.metadata || {},
            comments: [],
            history: [
                {
                    action: 'submitted',
                    at: payload.submitted_at || new Date().toISOString(),
                    by: payload.submitted_by_name || 'Unknown'
                }
            ]
        };
        requests.unshift(request);
        this.saveLocalRequests(requests);
        return request;
    }

    /**
     * Update locally stored request status when backend unavailable
     */
    updateLocalRequestStatus(requestId, status, comments = '', reviewer = null) {
        const requests = this.getLocalRequests();
        const index = requests.findIndex(req => req.id === requestId);
        if (index === -1) {
            return null;
        }

        const updatedRequest = {
            ...requests[index],
            status,
            reviewed_at: new Date().toISOString(),
            reviewed_by: reviewer?.id,
            reviewed_by_name: reviewer?.name,
            resolution_comments: comments
        };

        updatedRequest.history = [
            ...(requests[index].history || []),
            {
                action: status,
                at: updatedRequest.reviewed_at,
                by: reviewer?.name || 'Admin',
                comments
            }
        ];

        requests[index] = updatedRequest;
        this.saveLocalRequests(requests);
        return updatedRequest;
    }

    /**
     * Get locally cached approval requests
     */
    getLocalRequests() {
        try {
            const stored = localStorage.getItem(LOCAL_STORAGE_KEY);
            if (!stored) {
                return [];
            }
            return JSON.parse(stored);
        } catch (error) {
            console.error('Error loading employee update approval requests from local storage:', error);
            return [];
        }
    }

    /**
     * Persist approval requests to local storage
     */
    saveLocalRequests(requests) {
        try {
            localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(requests));
        } catch (error) {
            console.error('Error saving employee update approval requests to local storage:', error);
        }
    }
}

export default new ApprovalService();
